import { useTheme } from '@material-ui/core'
import MaterialTags from 'components/Common/ArticleSelector/MaterialTags'
import { MaterialKeywordDataGridEditor } from 'components/Common/ReactDataGridEditors/MaterialKeywordDataGridEditor'
import { NumberDataGridEditor } from 'components/Common/ReactDataGridEditors/NumberDataGridEditor'
import { TextFieldDataGridEditor } from 'components/Common/ReactDataGridEditors/TextFieldDataGridEditor'
import React from 'react'
import { Column } from 'react-data-grid'
import { useTranslation } from 'react-i18next'
import { PartTypeRowNumber } from '../components/PartTypeRowNumber'
import { BomItemUpdateGridModel } from './BomItemUpdateGridModel'

export const useBomItemGridColumns = () => {
  const { t } = useTranslation()
  const theme = useTheme()

  const columnsDefinition = React.useMemo(() => {
    const columns: Column<BomItemUpdateGridModel>[] = []

    columns.push({
      key: 'rowNumber',
      name: t('project:row-number', 'row'),
      maxWidth: 90,
      cellClass: 'vertical-center',
      headerCellClass: 'vertical-center',
      formatter: (props) => {
        return <PartTypeRowNumber bomItemId={props.row.id} />
      },
      resizable: false,
    })

    columns.push({
      key: 'thumbnail3d',
      editable: false,
      name: '',
      // eslint-disable-next-line react/display-name
      headerRenderer: () => <span />,
      resizable: false,
      formatter: (props) =>
        props.row?.thumbnail3d !== null && (
          <div className="vertical-center">
            <img
              loading="lazy"
              src={props.row?.thumbnail3d}
              style={{ display: 'block', width: '48px', height: '48px' }}
              alt={t('common:thumbnail')}
            />
          </div>
        ),
    })

    columns.push({
      key: 'name',
      name: t('common:name', 'name'),
      editable: false,
      width: theme.spacing(30),
      maxWidth: theme.spacing(40),
      headerCellClass: 'vertical-center',
      formatter: (props) => <>{props.row?.name}</>,
    })

    columns.push({
      key: 'originalFileName',
      name: t('common:file-name', 'file name'),
      editable: false,
      width: theme.spacing(30),
      maxWidth: theme.spacing(40),
      headerCellClass: 'vertical-center',
      formatter: (props) => <>{props.row?.originalFileName}</>,
    })

    columns.push({
      key: 'quantity',
      name: t('common:quantity'),
      headerCellClass: 'vertical-center',
      editor: (props) => (
        <NumberDataGridEditor
          {...props}
          rootElementClassName="vertical-center"
          allowNegative={false}
          decimalScale={0}
          isAllowed={(value) =>
            value.formattedValue === '' || value.floatValue < 1000000
          }
        />
      ),
      editable: (rowData) => !rowData.disabled && rowData.enableChangeQuantity,
      cellClass: 'right-align',
      maxWidth: 100,
      editorOptions: {
        renderFormatter: false,
      },
      resizable: false,
    })
    columns.push({
      key: 'keywords',
      name: t('common:quality'),
      width: '100%',
      maxWidth: 580,
      formatter: (props) => {
        return props.row[props.column.key] ? (
          <div
            style={{
              display: 'flex',
              width: '100%',
              gap: '.5em',
            }}
          >
            <MaterialTags selectedTokens={props.row[props.column.key]} />
          </div>
        ) : (
          <span />
        )
      },
      editor: (props) => (
        <MaterialKeywordDataGridEditor
          {...props}
          rootElementClassName="vertical-center"
          disableLabel
        />
      ),
      editable: (rowData) => !rowData.disabled,
      cellClass: 'flex-align-justify-center',
      headerCellClass: 'vertical-center',
      editorOptions: {
        renderFormatter: false,
      },
    })

    columns.push({
      key: 'comment',
      name: t('common:comment'),
      headerCellClass: 'vertical-center',
      editable: (rowData) => !rowData.disabled,
      editor: (props) => (
        <div className="vertical-center">
          <TextFieldDataGridEditor {...props} maxLength={140} />{' '}
        </div>
      ),
      sortable: false,
      width: 200,
    })

    return columns
  }, [t, theme])

  return columnsDefinition
}
